/**
 * Documents and processes that need to be demonstrable. Two tracks:
 *
 *   - `at-application`: dossier that the Dienst Toelating Arbeidskrachten
 *     expects when an uitlener registers between November and December 2026.
 *     Inleners do not register, so their `at-application` track is empty.
 *   - `ongoing`: what a controlled party should be able to produce on demand
 *     during periodic audits and inspections after 1 January 2027.
 */
export function generateEvidenceTracks(companyType) {
    if (companyType === 'uitlener') {
        return [
            {
                kind: 'at-application',
                title: 'Bij aanvraag van de toelating',
                lead: 'Onderstaande dossierset hoort bij jouw formele aanmelding tussen 1 november en 31 december 2026. Volledige aanlevering versnelt de behandeling en voorkomt aanvullende uitvragen.',
                items: UITLENER_AT_APPLICATION,
            },
            {
                kind: 'ongoing',
                title: 'Doorlopend, om de toelating te behouden',
                lead: 'Vanaf 1 januari 2027 volgen periodieke audits. Toezichthouders kunnen op elk moment de onderstaande documenten en processen opvragen. Houd ze actueel en centraal vindbaar.',
                items: UITLENER_ONGOING,
            },
        ];
    }
    return [
        {
            kind: 'ongoing',
            title: 'Doorlopend, om jouw controleplicht na te leven',
            lead: 'Inleners hoeven zich niet aan te melden voor de Wtta, maar krijgen vanaf 1 januari 2027 een actieve controleplicht op elke uitlener met wie zij samenwerken. Onderstaande dossierset moet je op verzoek van een toezichthouder direct kunnen tonen.',
            items: INLENER_ONGOING,
        },
    ];
}
const UITLENER_AT_APPLICATION = [
    {
        id: 'sna-inspection-report',
        title: 'Positief SNA-inspectierapport',
        description: 'Een geldig inspectierapport op basis van de herziene NEN 4400-1 norm. Dit is het belangrijkste bewijsstuk voor toelating.',
        practicalNote: 'Niet ouder dan 6 maanden op moment van aanmelding. Vraag jouw inspectie-instelling om een gedateerde verklaring.',
    },
    {
        id: 'kvk-extract',
        title: 'Recent KvK-uittreksel',
        description: 'Standaard inschrijving uit het Handelsregister, inclusief bestuurders, vestigingen en activiteiten.',
        practicalNote: 'Niet ouder dan 3 maanden. Online te bestellen via kvk.nl.',
    },
    {
        id: 'waarborgsom-proof',
        title: 'Bewijs van waarborgsom van 100.000 euro',
        description: 'Een bankgarantie, een gedeponeerde som of een schriftelijke vrijstellingsverklaring op basis van vier jaar schone bedrijfsvoering plus een geldig SNA-keurmerk. Voor startende ondernemingen is de waarborgsom 50.000 euro.',
        practicalNote: 'Reken bij een bankgarantie op 1 tot 3 procent van het bedrag aan jaarlijkse kosten. Vraag offertes op bij meerdere banken.',
    },
    {
        id: 'tax-conduct-statement',
        title: 'Verklaring betalingsgedrag belastingen',
        description: 'Verklaring van de Belastingdienst dat alle loonheffingen, omzetbelasting en premies tijdig zijn afgedragen.',
        practicalNote: 'Niet ouder dan 3 maanden. Aan te vragen via Mijn Belastingdienst Zakelijk. Doorlooptijd 5 tot 10 werkdagen.',
    },
    {
        id: 'payroll-administration',
        title: 'Loonadministratie laatste 12 maanden',
        description: 'Geanonimiseerde uitdraai met daarin de toegepaste CAO per detachering, de gehanteerde lonen en de afdrachten loonheffingen en premies.',
        practicalNote: 'Werk samen met jouw salarisadministratie of accountant aan een aanleverbaar exportformaat (csv of pdf).',
    },
    {
        id: 'active-detacheringen',
        title: 'Overzicht van lopende detacheringen',
        description: 'Lijst van alle inleners op de aanmeldingsdatum, inclusief contractnummer, startdatum, gehanteerde CAO en aantal arbeidskrachten per opdracht.',
        practicalNote: 'Genereer deze lijst kort vóór aanmelding zodat de gegevens actueel zijn.',
    },
    {
        id: 'governance-documents',
        title: 'Statuten en bestuursverklaring',
        description: 'Recente statuten van de rechtspersoon en een verklaring waarin het bestuur bevestigt dat zij verantwoordelijk is voor de naleving van de Wtta.',
        practicalNote: 'Tekst voor de bestuursverklaring is vormvrij. Houd het kort en specifiek: één A4 volstaat.',
    },
];
const UITLENER_ONGOING = [
    {
        id: 'sna-recertification',
        title: 'Geldig SNA-keurmerk, doorlopend',
        description: 'Periodieke hercertificering door jouw inspectie-instelling. Verloopt het keurmerk, dan vervalt automatisch ook de Wtta-toelating.',
        practicalNote: 'Plan minimaal één hercertificering per twaalf maanden. Stel een herinnering in zes maanden vooraf.',
    },
    {
        id: 'cao-evidence-per-job',
        title: 'CAO-toepassing per detachering',
        description: 'Voor elke arbeidskracht moet aantoonbaar de juiste inlenersbeloning zijn toegepast. Dat betekent: vastlegging van welke CAO van toepassing was, hoe het basisuurloon is bepaald en welke toeslagen zijn meegenomen.',
        practicalNote: 'Bouw deze borging in jouw urenregistratie of payroll-systeem in. Een spreadsheet houden gaat fout zodra je groeit.',
    },
    {
        id: 'g-account-transactions',
        title: 'G-rekening transactie-overzicht',
        description: 'Maandelijks overzicht van bedragen die op de G-rekening zijn ontvangen en daarna zijn afgedragen aan de Belastingdienst. Dit is jouw belangrijkste bewijs van afdrachten.',
        practicalNote: 'Vraag jouw bank om een maandelijks geautomatiseerd export. Bewaar minimaal zeven jaar.',
    },
    {
        id: 'incident-register',
        title: 'Klachten- en incidentregister',
        description: 'Vastlegging van klachten over arbeidsomstandigheden, beloning, huisvesting of veiligheid. Bij elk incident: datum, beschrijving, opvolgactie en datum van afhandeling.',
        practicalNote: 'Een eenvoudig register in jouw HR-systeem of een gedeelde spreadsheet voldoet, mits compleet en met audit-trail.',
    },
    {
        id: 'training-certificates',
        title: 'Trainingscertificaten van medewerkers',
        description: 'Voor risicovolle sectoren zoals bouw, logistiek en industrie zijn taakgerichte certificaten een audit-onderdeel. Voorbeelden: VCA, BHV, taalbeheersing.',
        practicalNote: 'Houd een centraal register bij met certificaatnummer, behaaldatum en geldigheidsduur.',
    },
    {
        id: 'housing-compliance',
        title: 'SNF-bewijs voor huisvesting arbeidsmigranten',
        description: 'Als je huisvesting regelt voor arbeidsmigranten, moet die voldoen aan SNF-norm. Bewaar het SNF-certificaat van jouw huisvestings-leveranciers of, als je zelf huisvest, jouw eigen certificering.',
        practicalNote: 'Alleen relevant als je arbeidsmigranten in dienst hebt. Zo niet, registreer dat expliciet zodat je dat ook kunt aantonen.',
    },
];
const INLENER_ONGOING = [
    {
        id: 'supplier-register',
        title: 'Register van uitleners met toelatingsstatus',
        description: 'Een actueel overzicht van alle uitleners met wie je samenwerkt, inclusief hun Wtta-toelatingsnummer, de datum van laatste verificatie en een link naar het toelatingsbewijs.',
        practicalNote: 'Update minimaal per kwartaal. Maak het deel van de standaard inkoop-procedure bij elke nieuwe leverancier.',
    },
    {
        id: 'periodic-verification-log',
        title: 'Verificatie-logboek',
        description: 'Per uitlener een logboek van wanneer je hun toelatingsstatus hebt gecontroleerd en wat de uitkomst was. Dit toont aan dat je jouw controleplicht actief invult.',
        practicalNote: 'Cadans van minimaal halfjaarlijks. Een geautomatiseerde check in jouw inkoop-systeem werkt het meest betrouwbaar.',
    },
    {
        id: 'wtta-contract-clauses',
        title: 'Contracten met Wtta-clausules',
        description: 'Alle lopende en nieuwe inhuurcontracten bevatten clausules die de uitlener verplichten tot Wtta-naleving en tot directe melding bij intrekking van de toelating.',
        practicalNote: 'Laat een addendum opstellen door jouw juridisch adviseur en rol dat eenmalig uit op alle bestaande contracten.',
    },
    {
        id: 'g-account-usage',
        title: 'Standaard werken via G-rekening',
        description: 'Bewijs dat je het loondeel van facturen op de G-rekening van de uitlener stort. Dit beperkt jouw aansprakelijkheid voor niet-afgedragen loonheffingen.',
        practicalNote: 'Maak G-rekening-gebruik standaard in jouw betalingsproces, niet optioneel per leverancier.',
    },
    {
        id: 'inkoop-audit-trail',
        title: 'Audit-trail van leverancierskeuzes',
        description: 'Bij elke selectie van een nieuwe uitlener: vastlegging dat de Wtta-toelating is gecontroleerd voorafgaand aan contractsluiting.',
        practicalNote: 'Een korte notitie of checkbox in jouw inkoopdossier volstaat, mits consequent toegepast.',
    },
];
//# sourceMappingURL=evidence.js.map